import type { User } from "firebase/auth";
import { callGetDungeonShopConfig, callSetDungeonShopConfig } from "@/cloud/game-callables";
import {
  createDefaultDungeonShopConfig,
  normalizeDungeonShopConfig,
  resolveMonsterShopEntry,
  type DungeonShopConfig,
} from "@/dungeon/dungeon-shop-types";
import { saveDungeonShopConfigLocal } from "@/dungeon/load-dungeon-shop-config";
import {
  hydrateGmMonsterCatalog,
  loadGmMonsterCatalog,
} from "@/gm/gm-monster-catalog";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function renderGmDungeonShopSectionHtml(): string {
  return `
    <section class="gm-section" id="gm-dungeon-shop-section">
      <p class="hint">ราคา/เพดานซื้อมอนสเตอร์และคูณพลังในหน้าแก้ด่าน · ทอง = coins · Cash ไม่จำกัด</p>
      <div id="gm-dungeon-shop-host"></div>
      <div class="home-actions">
        <button type="button" class="btn btn-primary" id="gm-dungeon-shop-save">บันทึกร้านด่าน</button>
      </div>
      <p class="hint" id="gm-dungeon-shop-status" role="status"></p>
    </section>
  `;
}

export async function prepareGmDungeonShopSection(): Promise<DungeonShopConfig> {
  await hydrateGmMonsterCatalog();
  try {
    const res = await callGetDungeonShopConfig();
    return normalizeDungeonShopConfig(res.config);
  } catch {
    return createDefaultDungeonShopConfig();
  }
}

function renderHost(config: DungeonShopConfig): string {
  const catalog = loadGmMonsterCatalog();
  const monsterRows = catalog
    .map((m) => {
      const entry = resolveMonsterShopEntry(config, m.id);
      return `
        <tr data-monster-id="${escapeHtml(m.id)}">
          <td>${escapeHtml(m.name)}</td>
          <td><input type="number" class="input input-sm" data-field="goldPrice" min="0" value="${entry.goldPricePerUnit}" /></td>
          <td><input type="number" class="input input-sm" data-field="cashPrice" min="0" value="${entry.cashPricePerUnit}" /></td>
          <td><input type="number" class="input input-sm" data-field="goldMax" min="0" value="${entry.goldMaxUnits}" /></td>
        </tr>`;
    })
    .join("");

  return `
    <div class="gm-dungeon-shop-defaults">
      <h3>ค่าเริ่มต้น (มอนสเตอร์ที่ยังไม่ตั้งรายตัว)</h3>
      <div class="gm-dungeon-shop-grid">
        <label class="field"><span class="field-label">ทอง/ตัว</span>
          <input type="number" class="input" id="gm-ds-def-gold" min="0" value="${config.defaultMonsterGoldPrice}" /></label>
        <label class="field"><span class="field-label">Cash/ตัว</span>
          <input type="number" class="input" id="gm-ds-def-cash" min="0" value="${config.defaultMonsterCashPrice}" /></label>
        <label class="field"><span class="field-label">เพดานทอง/ตัว</span>
          <input type="number" class="input" id="gm-ds-def-max" min="0" value="${config.defaultMonsterGoldMax}" /></label>
      </div>
      <h3>คูณพลังด่าน</h3>
      <div class="gm-dungeon-shop-grid">
        <label class="field"><span class="field-label">ขั้นต่อครั้ง</span>
          <input type="number" class="input" id="gm-ds-power-step" min="0.1" step="0.1" value="${config.powerStep}" /></label>
        <label class="field"><span class="field-label">ทอง/ขั้น</span>
          <input type="number" class="input" id="gm-ds-power-gold" min="0" value="${config.powerGoldPricePerStep}" /></label>
        <label class="field"><span class="field-label">Cash/ขั้น</span>
          <input type="number" class="input" id="gm-ds-power-cash" min="0" value="${config.powerCashPricePerStep}" /></label>
        <label class="field"><span class="field-label">เพดานทอง ×</span>
          <input type="number" class="input" id="gm-ds-power-gold-max" min="1" value="${config.goldMaxPowerMultiplier}" /></label>
        <label class="field"><span class="field-label">เพดาน Cash ×</span>
          <input type="number" class="input" id="gm-ds-power-cash-max" min="1" value="${config.cashMaxPowerMultiplier}" /></label>
      </div>
    </div>
    <h3>มอนสเตอร์ในแคตตาล็อก</h3>
    <p class="hint">ว่าง = ใช้ค่าเริ่มต้น · บันทึกแล้วมีผลทันทีในเกม</p>
    <table class="gm-dungeon-shop-table">
      <thead><tr><th>ชื่อ</th><th>ทอง</th><th>Cash</th><th>Max ทอง</th></tr></thead>
      <tbody>${monsterRows || `<tr><td colspan="4" class="hint">ยังไม่มีมอนสเตอร์ใน GM</td></tr>`}</tbody>
    </table>
  `;
}

function readConfigFromDom(root: HTMLElement, base: DungeonShopConfig): DungeonShopConfig {
  const num = (id: string, fallback: number) => {
    const v = Number(root.querySelector<HTMLInputElement>(`#${id}`)?.value);
    return Number.isFinite(v) ? v : fallback;
  };
  const monsters = [...root.querySelectorAll<HTMLTableRowElement>("tr[data-monster-id]")].map((row) => {
    const monsterId = row.dataset.monsterId ?? "";
    const goldPrice = Number(row.querySelector<HTMLInputElement>('[data-field="goldPrice"]')?.value);
    const cashPrice = Number(row.querySelector<HTMLInputElement>('[data-field="cashPrice"]')?.value);
    const goldMax = Number(row.querySelector<HTMLInputElement>('[data-field="goldMax"]')?.value);
    return {
      monsterId,
      goldPricePerUnit: Number.isFinite(goldPrice) ? goldPrice : base.defaultMonsterGoldPrice,
      cashPricePerUnit: Number.isFinite(cashPrice) ? cashPrice : base.defaultMonsterCashPrice,
      goldMaxUnits: Number.isFinite(goldMax) ? goldMax : base.defaultMonsterGoldMax,
    };
  });
  return normalizeDungeonShopConfig({
    ...base,
    defaultMonsterGoldPrice: num("gm-ds-def-gold", base.defaultMonsterGoldPrice),
    defaultMonsterCashPrice: num("gm-ds-def-cash", base.defaultMonsterCashPrice),
    defaultMonsterGoldMax: num("gm-ds-def-max", base.defaultMonsterGoldMax),
    powerStep: num("gm-ds-power-step", base.powerStep),
    powerGoldPricePerStep: num("gm-ds-power-gold", base.powerGoldPricePerStep),
    powerCashPricePerStep: num("gm-ds-power-cash", base.powerCashPricePerStep),
    goldMaxPowerMultiplier: num("gm-ds-power-gold-max", base.goldMaxPowerMultiplier),
    cashMaxPowerMultiplier: num("gm-ds-power-cash-max", base.cashMaxPowerMultiplier),
    monsters,
    updatedAt: Date.now(),
  });
}

export function bindGmDungeonShopSection(root: HTMLElement, _user: User, initial: DungeonShopConfig): void {
  const host = root.querySelector<HTMLElement>("#gm-dungeon-shop-host");
  const statusEl = root.querySelector<HTMLElement>("#gm-dungeon-shop-status");
  if (!host) return;

  let draft = initial;
  host.innerHTML = renderHost(draft);

  const setStatus = (msg: string) => {
    if (statusEl) statusEl.textContent = msg;
  };

  root.querySelector("#gm-dungeon-shop-save")?.addEventListener("click", () => {
    void (async () => {
      draft = readConfigFromDom(root, draft);
      try {
        await callSetDungeonShopConfig(draft);
        saveDungeonShopConfigLocal(draft);
        setStatus("บันทึกร้านด่านแล้ว");
      } catch (err) {
        setStatus(err instanceof Error ? err.message : "บันทึกไม่สำเร็จ");
      }
    })();
  });
}
